export interface PaymentError {
	message: string;
	type: 'error' | 'warning' | 'info';
	code?: string;
	retry?: boolean;
}

export class PaymentErrorHandler {
	static handleError(error: any): PaymentError {
		console.error('Payment Error:', error);

		// Erros de reCAPTCHA - expandidos
		if (error?.response?.data?.message?.includes('recaptcha') ||
			error?.response?.data?.message?.includes('captcha') ||
			error?.response?.data?.message?.includes('security') ||
			(error?.response?.status === 400 && error?.response?.data?.message?.includes('invalid')) ||
			error?.message?.includes('reCAPTCHA') ||
			error?.message?.includes('verificação de segurança') ||
			error?.message?.includes('Token de verificação de segurança não fornecido')) {
			return {
				message: 'Erro na verificação de segurança. Por favor, recarregue a página e tente novamente.',
				type: 'error',
				code: 'RECAPTCHA_ERROR',
				retry: true
			};
		}

		// Erros específicos de token vazio
		if (error?.message?.includes('Token do reCAPTCHA está vazio') ||
			error?.message?.includes('Token de verificação de segurança não fornecido')) {
			return {
				message: 'Token de segurança inválido. Recarregue a página e tente novamente.',
				type: 'error',
				code: 'RECAPTCHA_TOKEN_EMPTY',
				retry: true
			};
		}

		// Erros de falha na execução do reCAPTCHA
		if (error?.message?.includes('Falha ao executar reCAPTCHA') ||
			error?.message?.includes('reCAPTCHA não está disponível')) {
			return {
				message: 'Sistema de segurança temporariamente indisponível. Tente novamente em alguns instantes.',
				type: 'error',
				code: 'RECAPTCHA_EXECUTION_FAILED',
				retry: true
			};
		}

		// Erros de CPF
		if (error?.message === 'invalid CPF' || 'invalid CPF' === error?.response?.data?.message) {
			return {
				message: 'O CPF inserido é inválido. Por favor, verifique e tente novamente!',
				type: 'error',
				code: 'INVALID_CPF',
				retry: true
			};
		}

		// Erros de cartão
		if (error?.message === 'card_token is required' || 'card_token is required' === error?.response?.data?.message) {
			return {
				message: 'Ocorreu um erro ao processar o pagamento. Verifique se os dados do cartão foram inseridos corretamente e se há saldo disponível!',
				type: 'error',
				code: 'CARD_TOKEN_ERROR',
				retry: true
			};
		}

		// Erros de endereço
		if (error?.message === 'billingAddress is required' || 'billingAddress is required' === error?.response?.data?.message) {
			return {
				message: 'Endereço de cobrança não foi fornecido corretamente. Por favor, revise seus dados e tente novamente!',
				type: 'error',
				code: 'BILLING_ADDRESS_ERROR',
				retry: true
			};
		}

		// Erros de conta
		if (error?.message === 'account not found' || 'account not found' === error?.response?.data?.message) {
			return {
				message: 'Ocorreu um erro na operação, conta de recebimento não encontrada!',
				type: 'error',
				code: 'ACCOUNT_NOT_FOUND',
				retry: false
			};
		}

		// Erros de compra
		if (error?.message === 'purchase not found' || 'purchase not found' === error?.response?.data?.message) {
			return {
				message: 'Ocorreu um erro na operação, compra não encontrada!',
				type: 'error',
				code: 'PURCHASE_NOT_FOUND',
				retry: true
			};
		}

		// Erros de QR Code
		if (error?.message === 'Erro ao gerar QR Code do PIX') {
			return {
				message: 'Não foi possível gerar o QR Code. Tente novamente.',
				type: 'error',
				code: 'QR_CODE_ERROR',
				retry: true
			};
		}

		// Erros de servidor
		if (error?.response?.status === 500) {
			return {
				message: 'Ocorreu um erro interno no servidor, tente novamente mais tarde.',
				type: 'error',
				code: 'SERVER_ERROR',
				retry: true
			};
		}

		// Erros de rede
		if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('network')) {
			return {
				message: 'Erro de conexão. Verifique sua internet e tente novamente.',
				type: 'error',
				code: 'NETWORK_ERROR',
				retry: true
			};
		}

		// Erro genérico
		const genericError = error as { userMessage?: string };
		return {
			message: genericError?.userMessage || error?.response?.data?.message || error?.message || 'Ocorreu um erro na operação!',
			type: 'error',
			code: 'GENERIC_ERROR',
			retry: true
		};
	}

	static isRetryableError(error: any): boolean {
		const paymentError = this.handleError(error);
		return paymentError.retry || false;
	}

	static getErrorMessage(error: any): string {
		const paymentError = this.handleError(error);
		return paymentError.message;
	}

	static isRecaptchaError(error: any): boolean {
		const paymentError = this.handleError(error);
		return paymentError.code?.startsWith('RECAPTCHA') || false;
	}
}
